import { useState, useEffect, useCallback } from 'react';
import { authApi, tokenStore } from '../api/client';
import { User } from '../types';

interface AuthState {
  user: User | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  const setUser = (user: User | null) =>
    setState({ user, loading: false });

  // On mount: try to restore session from stored refresh token
  useEffect(() => {
    const rt = tokenStore.getRefresh();
    if (!rt) {
      setState({ user: null, loading: false });
      return;
    }
    // Attempt to get current user — the interceptor will refresh the access
    // token automatically if needed
    authApi.me()
      .then(setUser)
      .catch(() => {
        tokenStore.clear();
        setState({ user: null, loading: false });
      });
  }, []);

  // Listen for forced logout events (e.g. refresh token expired)
  useEffect(() => {
    const handler = () => setState({ user: null, loading: false });
    window.addEventListener('sf:logout', handler);
    return () => window.removeEventListener('sf:logout', handler);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const user = await authApi.login(email, password);
    setState({ user, loading: false });
    return user;
  }, []);

  const register = useCallback(
    async (email: string, password: string, fullName = '') => {
      await authApi.register(email, password, fullName);
      return login(email, password);
    },
    [login]
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setState({ user: null, loading: false });
  }, []);

  return { ...state, login, register, logout };
}
